import { useConfigure } from "react-instantsearch";
import type { PageHit } from "components/layout/Search/Results/models/pageHit";
import { Icon } from "library";
import React, { useMemo } from "react";
import {
  useIsApplePayAvailable,
  useIsGooglePayAvailable,
  useIsLoggedIn,
  usePermissions,
} from "hooks";
import { useProfile } from "hooks/firestore/v2/user/useProfile";
import type { Permissions } from "utilities/Auth/authSlice";
import { Row } from "./Row";
import { ResultsInIndex } from "./ResultsInIndex";
import { Highlight } from "./Highlight";
import { useNormalisedHits } from "components/layout/Search/Results/hooks/useNormalisedHits";

const getPagesFilters = (
  indexName: string,
  loggedIn: boolean,
  country: string,
  isApplePayAvailable: boolean,
  isGooglePayAvailable: boolean,
  permissions: Partial<Permissions>,
) => {
  const filters = [];

  if (indexName === "pages") {
    const countryFilter = country ? ` OR country:${country}` : "";
    if (!loggedIn) {
      filters.push(`country:false AND requiresSession:false`);
    } else if (!isApplePayAvailable && !isGooglePayAvailable) {
      filters.push(
        `(country:false${countryFilter}) AND requiresApplePay:false AND requiresGooglePay:false`,
      );
    } else if (!isApplePayAvailable) {
      filters.push(
        `(country:false${countryFilter}) AND requiresApplePay:false`,
      );
    } else if (!isGooglePayAvailable) {
      filters.push(
        `(country:false${countryFilter}) AND requiresGooglePay:false`,
      );
    } else {
      filters.push(`(country:false${countryFilter})`);
    }
  }

  // figure out required filters
  if (permissions?.viewLiveMarkets !== "GRANTED" && indexName === "matches") {
    filters.push(`bettingType:"prematch"`);
  }

  if (
    indexName === "matches" ||
    indexName === "outrights" ||
    indexName === "sports"
  ) {
    if (
      permissions.viewEsportsMarkets === "GRANTED" &&
      permissions.viewSportsMarkets !== "GRANTED"
    ) {
      filters.push("hub:esports");
    }

    if (
      permissions.viewEsportsMarkets !== "GRANTED" &&
      permissions.viewSportsMarkets === "GRANTED"
    ) {
      filters.push("hub:sports");
    }
  }

  return filters.join(" AND ");
};

export const PageResults = () => {
  const { items, results } = useNormalisedHits<PageHit>();

  const isLoggedIn = useIsLoggedIn();
  const permissions = usePermissions();
  const isApplePayAvailable = useIsApplePayAvailable();
  const isGooglePayAvailable = useIsGooglePayAvailable();
  const { profile } = useProfile();

  const indexName = "pages";

  const filters = useMemo(() => {
    return getPagesFilters(
      indexName,
      isLoggedIn,
      profile?.country || "",
      isApplePayAvailable,
      isGooglePayAvailable,
      permissions,
    );
  }, [
    indexName,
    isLoggedIn,
    profile?.country,
    isApplePayAvailable,
    isGooglePayAvailable,
    permissions,
  ]);

  useConfigure({
    filters,
  });

  return (
    <ResultsInIndex
      indexName={`Pages`}
      total={results?.nbHits || 0}
      pages={results?.nbPages || 0}
      perPage={results?.hitsPerPage || 0}
    >
      {(items || []).map((item) => {
        const titleHighlight = item.normalisedHighlights?.title?.[0];
        return (
          <Row
            key={item.objectID}
            icon={<Icon type={item.icon || "pageLink"} />}
            title={
              <Highlight
                text={item.title}
                matchedWords={titleHighlight?.matchedWords || []}
                html={titleHighlight?.value || ""}
              />
            }
            to={item.url}
          />
        );
      })}
    </ResultsInIndex>
  );
};
